/**
 * Pure helpers for the AI Insights tab. Logic is copied (not imported) from
 * `match-percentage-details.component.ts:330-384` and `:107-134` so the
 * slider can consume it without pulling DI into a presentational helper
 * and without touching the stable modal component.
 *
 * Duplication is intentional and approved (see approved plan,
 * `majestic-pondering-swan.md` — "duplicate AI helpers into a new util
 * without refactoring the existing modal"). If/when the modal is refactored
 * the two implementations should converge.
 */

export type RankingCriteriaLabel =
  | 'Title'
  | 'Job Title'  // display alias for `Title`; matches the same backend
                 //   "Job Title Alignment" / "Title Match" phrases.
                 //   Used as the visible row label in the V3 slider's
                 //   Match Breakdown while the criterion lookup still
                 //   passes the canonical `'Title'` key to `getCriteria`.
  | 'Skills'
  | 'Primary Skills'
  | 'Secondary Skills'
  | 'Experience'
  | 'Education'
  | 'Location'
  | 'Work Mode'
  | 'Engagement Type';

export interface RankingCriterion {
  label: RankingCriteriaLabel;
  /** 0-100 or `null` if no `(NN%)` token matched — bar should hide. */
  percentage: number | null;
}

/** Extract the first `(NN%)` token from a backend-generated phrase. */
export function extractPercentage(text: string): number | null {
  if (!text || typeof text !== 'string') return null;
  const match = text.match(/\((\d+)%\)/);
  return match ? Number(match[1]) : null;
}

/** Scan a combined strengths+weaknesses list for a phrase matching the
 *  requested criterion label. Backend emits several natural-language
 *  variants — each `if` mirrors the modal's exact match rules. Returns
 *  `null` when no percentage token is present (caller hides the bar). */
export function getCriteria(list: any[], type: RankingCriteriaLabel): number | null {
  if (!Array.isArray(list)) return null;
  for (const item of list) {
    if (!item || typeof item !== 'string') continue;

    if (type === 'Skills' && (item.includes('Skills Match') || item.includes('Primary Skills:'))) {
      return extractPercentage(item);
    }
    if (type === 'Primary Skills' && (item.includes('Primary Skills Match') || item.includes('Primary Skills:'))) {
      return extractPercentage(item);
    }
    if (type === 'Secondary Skills' && (item.includes('Secondary Skills Match') || item.includes('Secondary Skills:'))) {
      return extractPercentage(item);
    }
    if (type === 'Experience' && (item.includes('Experience Level') || item.includes('Experience Match'))) {
      return extractPercentage(item);
    }
    if (type === 'Title' && (item.includes('Job Title Alignment') || item.includes('Title Match'))) {
      return extractPercentage(item);
    }
    if (type === 'Education' && (item.includes('Education Match') || item.includes('Education Level'))) {
      return extractPercentage(item);
    }
    if (type === 'Location' && (item.includes('Location Compatibility') || item.includes('Location Match'))) {
      return extractPercentage(item);
    }
    if (type === 'Work Mode' && (item.includes('Work Mode Match') || item.includes('Work Mode Compatibility'))) {
      return extractPercentage(item);
    }
    if (type === 'Engagement Type' && (item.includes('Engagement Type') || item.includes('Engagement Match'))) {
      return extractPercentage(item);
    }
  }
  return null;
}

/** Strip the "Label:" prefix from a backend phrase so the strength/gap card
 *  shows the human description only, not the mechanical label. */
export function getOnlyText(raw: string | null | undefined): string {
  if (!raw) return '';
  const parts = String(raw).split(':');
  if (parts.length > 1) {
    parts.shift();
    return parts.join(':').trim();
  }
  return String(raw);
}

/** Build the `fetchRankingDetailsWithRetry` payload for a given surface.
 *  Mirrors `match-percentage-details.component.ts:getTypeAndGroupType()`
 *  exactly — do not change keys without updating both call sites.
 *
 *  Candidate-side surfaces (viewer is a recruiter):
 *    job-applicants | candidate-applied → APPLICANT_CANDIDATE
 *    talent-pool                        → TALENT_POOL
 *    suggested-candidates               → SUGGESTED_CANDIDATE
 *  Job-side surfaces (viewer is a candidate):
 *    jobsApplied                        → APPLICANT_JOB
 *    suggestedJobs                      → SUGGESTED_JOB
 */
export function getRankingPayload(
  pageFrom: string | undefined,
  jobId: string | undefined,
  candidateId: string | undefined
): { type: 'JOB' | 'CANDIDATE'; groupType: string; sourceEntityId: string; targetEntityId: string } | null {
  switch (pageFrom) {
    case 'job-applicants':
    // Contract Center / Billing Center candidate links: treated as an applicant
    // on the contract's job (same ranking as Job Applicants).
    case 'contract-candidates':
    case 'contract-work-orders':
    case 'billing-center':
    case 'candidate-applied':
      return { type: 'JOB', groupType: 'APPLICANT_CANDIDATE', sourceEntityId: jobId!, targetEntityId: candidateId! };
    case 'talent-pool':
      return { type: 'JOB', groupType: 'TALENT_POOL', sourceEntityId: jobId!, targetEntityId: candidateId! };
    case 'suggested-candidates':
      return { type: 'JOB', groupType: 'SUGGESTED_CANDIDATE', sourceEntityId: jobId!, targetEntityId: candidateId! };
    case 'candidates-invited':
      return { type: 'JOB', groupType: 'CANDIDATE_INVITED', sourceEntityId: jobId!, targetEntityId: candidateId! };
    case 'jobsApplied':
      return { type: 'CANDIDATE', groupType: 'APPLICANT_JOB', sourceEntityId: candidateId!, targetEntityId: jobId! };
    case 'suggestedJobs':
      return { type: 'CANDIDATE', groupType: 'SUGGESTED_JOB', sourceEntityId: candidateId!, targetEntityId: jobId! };
    case 'jobsInvited':
      return { type: 'CANDIDATE', groupType: 'JOB_INVITED', sourceEntityId: candidateId!, targetEntityId: jobId! };
  }
  return null;
}

/** Normalise a ranking response to the shape the AI tab template expects.
 *  Handles both the legacy `{ ranking }` object and the newer
 *  `{ rankings: [{ targetEntities: [...] }] }` array. Returns `null` when
 *  the response lacks usable ranking data. */
export function normaliseRanking(resData: any): {
  targetEntityId?: string;
  score: number;
  reason: string;
  strengths: string[];
  weaknesses: string[];
  updatedOn?: any;
  strengthsFeedback?: string;
  weaknessesFeedback?: string;
  overAllFeedback?: string;
  strengthsComments?: string;
  weaknessesComments?: string;
} | null {
  if (!resData) return null;

  // Old shape
  if (resData.ranking) {
    const r = resData.ranking;
    return {
      targetEntityId: r.targetEntityId,
      score: Number(r.score) || 0,
      reason: r.candidateReason || r.jobReason || r.reason || '',
      strengths: [...(r.pros || []), ...(r.jobStrengths || []), ...(r.strengths || [])],
      weaknesses: [...(r.cons || []), ...(r.jobWeaknesses || []), ...(r.weaknesses || [])],
      updatedOn: r.updatedOn,
      strengthsFeedback: r?.strengthsFeedback,
      weaknessesFeedback: r?.weaknessesFeedback,
      overAllFeedback: r?.overAllFeedback,
      strengthsComments: r?.strengthsComments,
      weaknessesComments: r?.weaknessesComments,
    };
  }

  // New shape — rankings array
  if (Array.isArray(resData.rankings) && resData.rankings.length > 0) {
    const first = resData.rankings[0];
    if (Array.isArray(first.targetEntities) && first.targetEntities.length > 0) {
      const t = first.targetEntities[0];
      return {
        targetEntityId: t.targetEntityId,
        score: Number(t.score ?? first.score) || 0,
        reason: t.candidateReason || t.jobReason || t.reason || first.reason || '',
        strengths: [
          ...(t.pros || []),
          ...(t.jobStrengths || []),
          ...(t.strengths || first.strengths || [])
        ],
        weaknesses: [
          ...(t.cons || []),
          ...(t.jobWeaknesses || []),
          ...(t.weaknesses || first.weaknesses || [])
        ],
        updatedOn: t.updatedOn || first.updatedOn,
        // Feedback can come back on EITHER level: per-target inside
        // `targetEntities[0]`, or on the ranking object itself. The older
        // modal only ever read the ranking level (it spreads `...firstRanking`
        // — see match-percentage-details.component.ts:206-230), which is why
        // saved feedback still shows there while this tab rendered blank.
        // Check the target first, then fall back to the ranking.
        strengthsFeedback: t?.strengthsFeedback ?? first?.strengthsFeedback,
        weaknessesFeedback: t?.weaknessesFeedback ?? first?.weaknessesFeedback,
        overAllFeedback: t?.overAllFeedback ?? first?.overAllFeedback,
        // Comments follow their rating. The panel resends the full state on
        // every click, so it must know the stored reason or it would send a
        // DISAGREE with no comment attached.
        strengthsComments: t?.strengthsComments ?? first?.strengthsComments,
        weaknessesComments: t?.weaknessesComments ?? first?.weaknessesComments,
      };
    }
  }

  return null;
}
